/* eslint-disable @typescript-eslint/no-explicit-any */
import httpClient from "@/services/index";
import type { IUserInfo } from "@/hooks/use-user-info";
import type Place from "@/model/place";
import { API_ENDPOINTS } from "./endpoints";

const getLocation = async (queryString: string, userInfo: IUserInfo) => {
  const url = API_ENDPOINTS.LOCATION.PREDICTIONS;
  const data = {
    input: queryString,
  };

  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url,
    headers: {
      Authorization: `Bearer ${userInfo.accessToken}`,
      GroupId: userInfo.groupId,
    },
    data,
  };
  const response = await httpClient.request(config);
  const predictions = response?.data?.predictions || [];

  // Get lat/lon for each prediction
  const locationsWithLatLon = await Promise.all(
    predictions.map(async (prediction: Place) => {
      const latLonData = await getLatLon(prediction, userInfo);
      return {
        place_id: prediction.place_id,
        description: prediction.description,
        reference: prediction.reference,
        lat: latLonData.lat,
        lng: latLonData.lng,
        postal_code: latLonData?.postal_code,
      };
    }),
  );

  return {
    data: locationsWithLatLon,
    next: null,
  };
};

type AddressComponent = {
  types?: string[];
  short_name?: string;
};

type LatLon = {
  lat: number;
  lng: number;
  postal_code: string;
};

export const toPostalCode = (addressComponents: unknown): string => {
  if (!Array.isArray(addressComponents)) return "";

  const postalCode = (addressComponents as AddressComponent[]).find(
    (component) => component?.types?.includes("postal_code"),
  );

  return postalCode?.short_name ?? "";
};

const getLatLon = async (
  place: Place,
  userInfo: IUserInfo,
): Promise<LatLon> => {
  const url = API_ENDPOINTS.LOCATION.LAT_LONG;
  const data = {
    placeId: place.place_id,
  };

  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url,
    headers: {
      Authorization: `Bearer ${userInfo.accessToken}`,
      GroupId: userInfo.groupId,
    },
    data,
  };

  const response = await httpClient.request(config);
  const result = response?.data?.result;

  return {
    lat: result?.geometry?.location?.lat ?? 0,
    lng: result?.geometry?.location?.lng ?? 0,
    postal_code: toPostalCode(result?.address_components),
  };
};

export { getLocation, getLatLon };

/*
{
            "reference": "ChIJGzE9DS1l44kRoOhiASS_fHg",
            "types": [
                "political",
                "locality",
                "geocode"
            ],
            "matched_substrings": [
                {
                    "offset": 0,
                    "length": 6
                }
            ],
            "terms": [
                {
                    "offset": 0,
                    "value": "Boston"
                },
                {
                    "offset": 8,
                    "value": "MA"
                },
                {
                    "offset": 12,
                    "value": "USA"
                }
            ],
            "structured_formatting": {
                "main_text_matched_substrings": [
                    {
                        "offset": 0,
                        "length": 6
                    }
                ],
                "secondary_text": "MA, USA",
                "main_text": "Boston"
            },
            "description": "Boston, MA, USA",
            "place_id": "ChIJGzE9DS1l44kRoOhiASS_fHg"
        }
*/

export const getLocationDataFromCoordinates = async (
  latitude: number,
  longitude: number,
  accessToken: string,
): Promise<any> => {
  const url = API_ENDPOINTS.LOCATION.GEOCODE;
  const payload = {
    longitude,
    latitude,
  };
  const config = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      "Accept-Version": "1.0",
    },
  };
  return httpClient.post(url, payload, config).then((response) => response);
};
